import React from "react";
import {
  Input,
  Textarea,
  SelectItem,
  Tooltip,
  Button,
} from "@heroui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  UserIcon,
  PhoneIcon,
  GlobeAltIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { State, ValidationErrors } from "../../types/types";
import { TFunction } from "react-i18next";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";
import AppSelect from "../common/appSelect";

const labelClass =
  "!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0 after:hidden";

const contactFieldClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const textareaClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const contactSelectClassNames = {
  base: "gap-1",
  label: labelClass,
  trigger:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] data-[focus=true]:border-[#2563EB] data-[open=true]:border-[#2563EB]",
  value:
    "text-xs sm:text-sm leading-5 text-[#18181B] group-data-[has-value=false]:text-slate-400 lg:group-data-[has-value=false]:text-[#71717A]",
  selectorIcon: "text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const tertiaryButtonClass =
  "h-10 rounded-2xl lg:rounded-3xl bg-[#EBEBEC] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-[#18181B] hover:bg-[#dcdcde] active:scale-[0.98] transition-all";

interface ContactDetailsStageProps {
  t: TFunction<"signUpSetupStages", undefined>;
  state: State;
  validationErrors: ValidationErrors;
  handleFormChange: (value: string, field: string) => void;
  updateSocialMediaLink: (
    index: number,
    field: "type" | "url",
    value: string,
  ) => void;
  addSocialMediaLink: () => void;
  removeSocialMediaLink: (index: number) => void;
  socialMediaPlatforms: string[];
  handleNext: (e: React.FormEvent | any, nextStage: string | any) => void;
  handleBack: () => void;
}

const ContactDetailsStage: React.FC<ContactDetailsStageProps> = ({
  t,
  state,
  validationErrors,
  handleFormChange,
  updateSocialMediaLink,
  addSocialMediaLink,
  removeSocialMediaLink,
  socialMediaPlatforms,
  handleNext,
  handleBack,
}) => {
  return (
    <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
      {/* LEFT COLUMN */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="
            flex min-h-dvh flex-col justify-between
            px-6 py-6 sm:px-12 sm:py-8
            lg:py-9
            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
            [@media(max-height:700px)]:lg:py-3
          "
        >
          {/* Header: logo */}
          <div className="flex w-full items-center justify-between">
            <img
              src={hoowlrLogo}
              alt="Hoowlr"
              className="
                h-9 w-auto object-contain object-left
                lg:h-16 lg:w-48
                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                [@media(max-height:700px)]:lg:h-10
              "
            />
          </div>

          {/* Centered content */}
          <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
            <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                {t("contactDetails.title")}
              </h1>
              <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                {t("contactDetails.subtitle")}
              </p>
            </div>

            <form
              noValidate
              onSubmit={(e) => handleNext(e, "profileImageUpload")}
              className="
                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
              "
            >
              <div
                className="
                  flex flex-col gap-3 lg:gap-4
                  [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                "
              >
                <Input
                  type="email"
                  name="email"
                  label={t("contactDetails.email")}
                  labelPlacement="outside"
                  placeholder={t("contactDetails.emailPlaceholder")}
                  value={state.formData.email ?? ""}
                  onValueChange={(value) => handleFormChange(value, "email")}
                  isRequired
                  isReadOnly
                  isInvalid={!!validationErrors["email"]}
                  errorMessage={validationErrors["email"]}
                  className="w-full"
                  classNames={contactFieldClassNames}
                  startContent={
                    <UserIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                  }
                />
                <Input
                  type="tel"
                  name="phone"
                  label={t("contactDetails.phone")}
                  labelPlacement="outside"
                  placeholder={t("contactDetails.phonePlaceholder")}
                  value={state.formData.phone ?? ""}
                  onValueChange={(value) => handleFormChange(value, "phone")}
                  isInvalid={!!validationErrors["phone"]}
                  errorMessage={validationErrors["phone"]}
                  className="w-full"
                  classNames={contactFieldClassNames}
                  startContent={
                    <PhoneIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                  }
                />
                <Textarea
                  name="bio"
                  label={t("contactDetails.bio")}
                  labelPlacement="outside"
                  placeholder={t("contactDetails.bioPlaceholder")}
                  value={state.formData.bio ?? ""}
                  onValueChange={(value) => handleFormChange(value, "bio")}
                  maxLength={1000}
                  isInvalid={!!validationErrors["bio"]}
                  errorMessage={validationErrors["bio"]}
                  className="w-full"
                  classNames={textareaClassNames}
                />

                <div className="space-y-3 pt-2">
                  <h3 className="text-xs sm:text-sm font-semibold lg:font-medium text-slate-700 lg:text-[#18181B] flex items-center gap-1.5">
                    <span>{t("contactDetails.socialMediaLinks")}</span>
                    <Tooltip
                      className="w-[280px]"
                      placement="right"
                      content={
                        <div className="text-xs p-1">
                          <span className="font-semibold block mb-0.5">
                            {t("socialMediaPublic")}
                          </span>
                          <span>{t("socialMediaDescription")}</span>
                        </div>
                      }
                    >
                      <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                    </Tooltip>
                  </h3>

                  {(state.formData.profileType === "FIGHTER"
                    ? state.formData.fighterDto?.socialMediaLinks
                    : state.formData.managerDto?.socialMediaLinks
                  )?.map((link, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-3 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-slate-50 lg:bg-[#F9FAFB] p-3.5"
                    >
                      <AppSelect
                        name={`socialMediaLinkType-${index}`}
                        label={t("contactDetails.socialMediaPlatform")}
                        labelPlacement="outside"
                        placeholder={t(
                          "contactDetails.socialMediaPlatformPlaceholder"
                        )}
                        selectedKeys={new Set(link.type ? [link.type] : [])}
                        onSelectionChange={(keys) => {
                          const selected = Array.from(keys)[0] as string | undefined;
                          updateSocialMediaLink(index, "type", selected || "");
                        }}
                        isInvalid={
                          !!validationErrors[`socialMediaLinks.${index}.type`]
                        }
                        errorMessage={
                          validationErrors[`socialMediaLinks.${index}.type`]
                        }
                        className="w-full"
                        classNames={contactSelectClassNames}
                        startContent={
                          <GlobeAltIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                        }
                      >
                        {socialMediaPlatforms.map((platform) => (
                          <SelectItem
                            key={platform}
                            isDisabled={
                              platform !== link.type &&
                              (state.formData.profileType === "FIGHTER"
                                ? state.formData.fighterDto?.socialMediaLinks
                                : state.formData.managerDto?.socialMediaLinks
                              )?.some(
                                (item, i) => item.type === platform && i !== index
                              )
                            }
                          >
                            {platform}
                          </SelectItem>
                        ))}
                      </AppSelect>

                      <Input
                        type="url"
                        name={`socialMediaLinkUrl-${index}`}
                        label={t("contactDetails.socialMediaUrl", {
                          platform: link.type || "Platform",
                        })}
                        labelPlacement="outside"
                        placeholder={t("contactDetails.socialMediaUrlPlaceholder", {
                          platform: link.type || "Platform",
                        })}
                        value={link.url}
                        onValueChange={(value) =>
                          updateSocialMediaLink(index, "url", value)
                        }
                        isInvalid={
                          !!validationErrors[`socialMediaLinks.${index}.url`]
                        }
                        errorMessage={
                          validationErrors[`socialMediaLinks.${index}.url`]
                        }
                        className="w-full"
                        classNames={contactFieldClassNames}
                        startContent={
                          <GlobeAltIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                        }
                      />

                      <Button
                        onPress={() => removeSocialMediaLink(index)}
                        color="danger"
                        variant="flat"
                        size="sm"
                        className="self-end rounded-xl bg-red-50 text-red-500 hover:bg-red-100 text-xs font-medium px-3 py-1.5 transition-all"
                      >
                        {t("contactDetails.removeSocialMediaLink")}
                      </Button>
                    </div>
                  ))}

                  <Button
                    onPress={addSocialMediaLink}
                    variant="bordered"
                    className="w-full rounded-xl border border-[#0485F7] text-[#0485F7] text-xs sm:text-sm font-semibold h-9 hover:bg-[#0485F7]/10 transition-all"
                  >
                    {t("contactDetails.addSocialMediaLink")}
                  </Button>
                </div>
              </div>

              {/* Footer: Back / Next */}
              <div className="flex w-full items-center justify-between gap-3 pt-4 sm:pt-6 [@media(max-height:700px)]:pt-3">
                <Button
                  onPress={handleBack}
                  variant="flat"
                  startContent={<ArrowLeftIcon className="h-4 w-4" />}
                  className={tertiaryButtonClass}
                >
                  {t("navigation.back")}
                </Button>

                <Button
                  type="submit"
                  endContent={<ArrowRightIcon className="h-4 w-4" />}
                  className="h-10 rounded-2xl lg:rounded-3xl bg-[#0485F7] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {t("navigation.next")}
                </Button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
            {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
          </p>
        </div>
      </div>

      {/* RIGHT BANNER (hidden below lg) */}
      <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
          <img
            src={loginBanner}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsStage;
